import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Drawer } from '@/components/Drawer/Drawer';
import i18n from '@/i18n';
import { Input } from '@/components/UI/Input/Input';
import { Chip } from '@/components/UI/Chip/Chip';
import { PaymentAgreementText } from '@/components/Payment/PaymentAgreementText';
import { PaymentMethods } from '@/components/Payment/PaymentMethods';
import {
  CreatePaymentResponse,
  PaymentMethod,
  PaymentService,
  PaymentStatus,
  TerminalPaymentTargetType,
} from '@/api/types/payments.types';
import * as S from '@/components/Payment/AddBalance.styles';
import { amounts } from '@/components/Payment/AddBalance.constants';
import { GetUserResponse } from '@/api/types/user.types';
import { GET_USER } from '@/api/queries/user.queries';
import { AdService } from '@/api/types/services.types';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { mapPaymentDataToUrl } from '@/views/Wallet/Finish.helpers';
import { Popup } from '@/components/UI/Popup/Popup';
import { MODAL_ANIMATION_DURATION } from '@/styles/constants';
import { PaymentInTerminals } from '@/components/Payment/PaymentInTerminals';
import { useTerminalPayment } from '@/components/Payment/useTerminalPayment';
import { useCreatePayment } from '@/hooks/useCreatePayment';
import { MutationError } from '@/api/types/common.types';
import { useNewWindow } from '@/hooks/useNewWindow';
import { usePostMessage } from '@/hooks/usePostMessage';
import { AdBundle } from '@/api/types/categories.types';
import { useBridgeAPI, useBridgeState } from '@/bridge/hooks';
import { useWebViewPostMessage } from '@/bridge/hooks/useWebViewPostMessage';
import { EventName, Request } from '@/bridge/types';
import { useWebViewPaymentModalStore } from '@/store/useWebViewPaymentModalStore';
import { buildWebViewAddBalancePaymentData } from '@/components/Payment/WebViewPaymentStatusModal/WebViewPaymentStatusModal.helpers';
import { sleep } from '@/utils/helpers';
import { getRefetchDelay } from '@/api/helpers/payments.helpers';
import { usePlatformSource } from '@/hooks/usePlatformSource';

export const addBalanceFormSchema = z.object({
  amount: z.string().min(1, i18n.t('common.required_field')),
});

export type AddBalanceFormFields = z.infer<typeof addBalanceFormSchema>;

type Props = {
  isOpen: boolean;
  toggle: (v?: boolean) => void;
  service?: AdService | null;
  bundle?: AdBundle | null;
};

export const AddBalance = ({ isOpen, toggle, service, bundle }: Props) => {
  const { t } = useTranslation();
  const {
    newWindow: paymentWindow,
    close: closePaymentWindow,
    open: openPaymentWindow,
  } = useNewWindow();
  const source = usePlatformSource();
  const openWebViewPaymentModal = useWebViewPaymentModalStore(
    state => state.open
  );

  const [payment, setPayment] = useState(PaymentMethod.My_card);
  const [cardId, setCardId] = useState<string | undefined>(undefined);
  const [errorState, setErrorState] = useState({
    message: '',
    isOpen: false,
  });

  const { isWebView } = useBridgeState();
  const bridgeAPI = useBridgeAPI();

  const { isTerminalOpen, toggleTerminal, isTerminal } = useTerminalPayment({
    payment,
  });

  const {
    createPayment,
    loading: isPaymentLoading,
    paymentProvider,
  } = useCreatePayment({
    onSuccess: onSuccessPayment,
    onError: onCreatePaymentError,
    onRedirect: onCreatePaymentRedirect,
  });

  function handleWebViewPostMessage(
    params: Request.Params[EventName.PaymentStatus]
  ) {
    if (!isOpen) return;

    const delay = getRefetchDelay(paymentProvider);

    openWebViewPaymentModal(params);

    if (params.status === PaymentStatus.Success) {
      toggle(false);

      sleep(delay).then(() => {
        refetchUserPaymentInfo();
      });
    }
  }

  function handlePostMessage() {
    if (!isOpen || payment !== PaymentMethod.Card) return;

    const delay = getRefetchDelay(paymentProvider);

    sleep(delay).then(() => {
      refetchUserPaymentInfo();
      toggle(false);
    });
  }

  useWebViewPostMessage<Request.Params[EventName.PaymentStatus]>({
    eventName: 'paymentStatus',
    handler: handleWebViewPostMessage,
  });

  usePostMessage({
    postMessageName: 'paymentFinished',
    handler: handlePostMessage,
  });

  function openErrorPopup(message: string) {
    setErrorState(prev => ({ ...prev, isOpen: true, message }));
  }

  function closeErrorPopup() {
    setErrorState(prev => ({ ...prev, isOpen: false }));
    setTimeout(
      () => setErrorState(prev => ({ ...prev, message: '' })),
      MODAL_ANIMATION_DURATION
    );
  }

  const { control, handleSubmit, setValue, watch } =
    useForm<AddBalanceFormFields>({
      resolver: zodResolver(addBalanceFormSchema),
      defaultValues: { amount: amounts[0].toString() },
    });

  const watchedAmount = watch('amount');

  const { refetch: refetchUserPaymentInfo } = useQuery<GetUserResponse>(
    GET_USER,
    { skip: true }
  );

  function onCreatePaymentError(
    status: PaymentStatus,
    errors?: MutationError[]
  ) {
    closePaymentWindow();
    if (errors?.length && status === PaymentStatus.Failure) {
      openErrorPopup(errors[0].message);
    }
  }

  function onCreatePaymentRedirect(redirectUrl: string) {
    if (isWebView) {
      bridgeAPI.openPayment(redirectUrl);
      return;
    }

    if (paymentWindow) {
      paymentWindow.location.href = redirectUrl;
    }
  }

  function onSuccessPayment(data: CreatePaymentResponse) {
    const { paymentDate, paymentLegacyId, targetLegacyId } =
      data.createPayment.entity;
    if (paymentDate && paymentLegacyId && targetLegacyId) {
      setPayment(PaymentMethod.Wallet);
      const url = mapPaymentDataToUrl({
        id: paymentLegacyId,
        price: watchedAmount.toString(),
        date: paymentDate,
        paymentMethod: payment,
        privateAccountId: targetLegacyId,
      });

      if (url && paymentWindow) {
        paymentWindow.location.href = url;
        refetchUserPaymentInfo();
        toggle(false);

        return;
      }

      if (isWebView) {
        const params = buildWebViewAddBalancePaymentData(t, {
          paymentDate,
          paymentId: paymentLegacyId,
          targetId: targetLegacyId,
          amount: watchedAmount,
        });
        openWebViewPaymentModal(params);
        refetchUserPaymentInfo();
        toggle(false);
        return;
      }

      closePaymentWindow();
    }
  }

  function onSubmit(data: AddBalanceFormFields) {
    if (isTerminal) {
      return toggleTerminal();
    }
    createPayment({
      variables: {
        serviceId: PaymentService.Add_balance_custom,
        paymentMethod: payment,
        amount: Number(data.amount),
        source,
        ...(payment === PaymentMethod.My_card && cardId && { cardId }),
      },
    });

    if (!isWebView) openPaymentWindow();
  }

  useEffect(() => {
    const defaultValue = amounts[0].toString();

    if (service) {
      setValue('amount', service.price.toString());
      return;
    }

    if (bundle) {
      setValue('amount', bundle.price.toString());
      return;
    }

    setValue('amount', defaultValue);
  }, [service, bundle]);

  const payButtonTitle = isTerminal
    ? t('common.terminal_top_up')
    : t('pages.cabinet.add');

  return (
    <Drawer
      dataTestId={AdPageTestIds.addBalanceDrawer.drawer}
      isOpen={isOpen}
      close={toggle}
      title={t('pages.cabinet.add_private_account')}
      hasBottomMargin={false}
    >
      <S.Form noValidate>
        <Controller
          name="amount"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              isBigText
              label={t('pages.cabinet.add_amount')}
              helperText={fieldState.error?.message}
              hasError={!!fieldState.error}
            />
          )}
        />

        <S.Chips>
          {amounts.map(amount => (
            <Chip
              key={amount}
              appearance={
                watchedAmount === amount.toString()
                  ? 'secondaryTransparent'
                  : 'default'
              }
              onClick={() => setValue('amount', amount.toString())}
            >
              {amount} AZN
            </Chip>
          ))}
        </S.Chips>
      </S.Form>

      <PaymentMethods
        payment={payment}
        setPayment={setPayment}
        setCardId={setCardId}
        isAddBalanceOpen={isOpen}
      />

      <S.SubmitButton
        data-testid={AdPageTestIds.addBalanceDrawer.button}
        appearance="secondary"
        isFullWidth
        onClick={handleSubmit(onSubmit)}
        disabled={isPaymentLoading}
      >
        {payButtonTitle}
      </S.SubmitButton>

      <PaymentAgreementText />

      <Popup
        dataTestId={AdPageTestIds.popup.errorAddBalance}
        isOpen={errorState.isOpen}
        close={closeErrorPopup}
        description={errorState.message}
        actions={{
          left: { text: t('common.got_it'), onClick: closeErrorPopup },
        }}
      />

      <PaymentInTerminals
        isOpen={isTerminalOpen}
        toggle={toggleTerminal}
        onOk={() => toggleTerminal(false)}
        paymentTargetType={TerminalPaymentTargetType.Balance}
      />
    </Drawer>
  );
};
